import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button, TextField, Typography, Box, Card, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { styled } from '@mui/system';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import GeolocationHandler from './LiveDispatch/GeolocationHandler';
import comms from '../comms';
import { getDirections } from '../../services/directionsService';

// Create vehicle icon SVG (same as LoopedWaypointMap)
const createVehicleIcon = (color = '#1F2937') => {
  const svgIcon = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <g>
        <!-- Vehicle shadow -->
        <ellipse cx="20" cy="35" rx="16" ry="3" fill="#000000" opacity="0.2"/>
        <!-- Vehicle body -->
        <rect x="8" y="15" width="24" height="15" rx="3" fill="${color}" stroke="#FFFFFF" stroke-width="2"/>
        <!-- Vehicle front -->
        <rect x="10" y="10" width="20" height="8" rx="4" fill="${color}" stroke="#FFFFFF" stroke-width="2"/>
        <!-- Wheels -->
        <circle cx="13" cy="30" r="4" fill="#374151" stroke="#FFFFFF" stroke-width="2"/>
        <circle cx="27" cy="30" r="4" fill="#374151" stroke="#FFFFFF" stroke-width="2"/>
        <!-- Windows -->
        <rect x="12" y="12" width="16" height="6" rx="2" fill="#E5E7EB" stroke="#D1D5DB" stroke-width="1"/>
        <!-- Windshield -->
        <rect x="14" y="14" width="12" height="3" rx="1" fill="#F3F4F6"/>
        <!-- Headlights -->
        <circle cx="11" cy="14" r="1.5" fill="#FEF3C7" stroke="#F59E0B" stroke-width="0.5"/>
        <circle cx="29" cy="14" r="1.5" fill="#FEF3C7" stroke="#F59E0B" stroke-width="0.5"/>
        <!-- Direction indicator (small arrow) -->
        <polygon points="20,5 23,10 17,10" fill="#3B82F6" stroke="#FFFFFF" stroke-width="1"/>
      </g>
    </svg>
  `)}`;
  
  if (!window.google?.maps) return { url: svgIcon };
  return {
    url: svgIcon,
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(20, 20)
  };
};

// Create finish flag marker with green circle and checkered flag
const createFinishFlagIcon = () => {
  const svgIcon = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <!-- Shadow filter -->
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="1" dy="2" stdDeviation="2" flood-color="#000000" flood-opacity="0.3"/>
        </filter>
      </defs>
      
      <!-- Green circle with white outline and shadow (smaller) -->
      <circle cx="14" cy="14" r="12" fill="#10B981" stroke="#FFFFFF" stroke-width="2" filter="url(#shadow)"/>
      
      <!-- Checkered flag icon (larger, extending beyond circle) -->
      <g transform="translate(4, 4) scale(0.2)">
        <path style="fill:#B0B0B0;" d="M13.56,15.85c-0.18-1.27-0.39-6.82-5.23-6.33C4.28,9.93,4.5,14.28,4.89,16.3
	c0.22,1.15,13.22,104.18,13.39,105.28c0.23,1.47,2.28,3,4.62,2.76c2.34-0.23,3.71-2.05,3.66-3.78
	C26.53,119.24,13.79,17.58,13.56,15.85z"/>
<g>
	<path style="fill:#C8C8C8;" d="M21.41,116.33c-0.62,0-1.16-0.46-1.24-1.09L7.07,14.12c-0.09-0.68,0.39-1.31,1.08-1.4
		c0.69-0.09,1.31,0.4,1.4,1.08l13.1,101.12c0.09,0.68-0.39,1.31-1.08,1.4C21.52,116.32,21.47,116.33,21.41,116.33z"/>
</g>
<path style="fill:#9B9B9B;" d="M16.01,14.19l-2.45,1.66c0,0,7.97,70.36,8.27,70.46c0.3,0.1,17.44-12.91,32.12-13.01
	c13.57-0.09,17.47,6.14,38.07,6.89c11.55,0.42,23.91-7.28,23.91-7.28L16.01,14.19z"/>
<path style="fill:#DACEC9;" d="M16.01,18.13l-0.46-3.64c0,0,10.08-8.14,22.74-7.45s25.51,12.08,43.46,10.15
	c10.61-1.14,16.54-6.16,16.54-6.16l12.79,4.59l9.46,39.21l-10.27,9.81c0,0-77.69-27.15-78.37-27.95
	C31.22,35.9,16.01,18.13,16.01,18.13z"/>
<polygon style="fill:#2F2F2F;" points="17.9,27.78 15.15,29.96 16.64,42.84 20,40.03 "/>
<polygon style="fill:#2F2F2F;" points="20.45,54.48 18.16,56.11 19.82,70.3 23.66,67.98 "/>
<path style="fill:#2F2F2F;" d="M37.55,73.79c0.06,0.43,0.45,3.45,0.45,3.45s6.18-2.78,11.53-3.43c4.38-0.54,7.63-0.4,7.63-0.4
	l-0.44-2.97l-11.31-0.78L37.55,73.79z"/>
<path style="fill:#2F2F2F;" d="M74.22,74.39l0.56,3.34c0,0,2.56,0.81,7.2,1.56c5.83,0.95,9.48,0.96,9.99,0.94l-0.56-4.85
	l-10.37-2.76L74.22,74.39z"/>
<path style="fill:#2F2F2F;" d="M107.76,73.62l0.51,3.12c0,0,4.02-1.36,8.93-4.24c3.72-2.19,6.32-4.15,6.35-4.41
	c0.03-0.26-0.58-2.92-0.71-3.34c-0.13-0.42-1.22-1.35-1.22-1.35L107.76,73.62z"/>
<path style="fill:#F1EDEC;" d="M23.69,81.19l-7.68-63.06c0,0,9.51-9.49,23.2-7.67s29.1,11.31,42.56,9.49
	c13.46-1.83,23.49-9.6,23.49-9.6l2.28,2.4l11.18,46.2l-10.95,14.69c0,0-7.64,3.56-18.02,2.99c-21.71-1.19-23.81-7.74-40.4-5.94
	c-4.4,0.48-8.65,1.84-12.4,3.36C29.23,77.17,23.69,81.19,23.69,81.19z"/>
<path style="fill:#434343;" d="M28.24,8.23l1.72,13.56c0,0-4.41,1.31-7.76,3.07c-2.54,1.33-4.97,3.45-4.97,3.45l1.58,12.72
	c0,0,2.98-2.11,6.56-3.75c2.4-1.1,5.91-2.04,5.91-2.04l2.07,12.99c0,0-4.69,1.61-7.34,3.02s-5.55,3.22-5.55,3.22l1.74,14.4
	c0,0,4.09-2.45,5.99-3.39c3.12-1.54,7.68-3.28,7.68-3.28l1.7,11.59c0,0,5.94-2.14,9.88-2.76c5.35-0.83,9.3-0.61,9.3-0.61
	l-1.62-10.74c0,0,3.78-0.22,8.36,0.79c4.17,0.92,8.62,1.96,8.62,1.96l2.14,11.94c0,0,2.83,0.91,8.06,1.67
	c5.24,0.76,9.28,0.64,9.28,0.64L90,64.89c0,0,4.12-0.28,7.85-0.85c3.73-0.56,7.74-1.94,7.74-1.94l2.17,11.54
	c0,0,4.29-1.92,7.95-4.12c4.43-2.66,7.13-4.82,7.13-4.82l-2.3-9.87c0,0-4.16,2.33-6.84,3.69c-3.48,1.75-7.83,3.24-7.83,3.24
	l-2.31-12.51c0,0,4.4-1.8,7.17-3.16c3.11-1.52,6.75-4.04,6.75-4.04l-3.39-13.96c0,0-3.55,2.26-6.8,4.11
	c-2.66,1.51-6.15,3.33-6.55,3.22s-2.69-10.79-2.6-11.07c0.04-0.13,3.14-1.54,7.44-4.37c2.28-1.5,5.52-4.34,5.52-4.34
	s-1.79-9.93-2.11-11.05s-1.41-1.76-2.52-0.67s-4.25,3.93-5.69,5.17s-5.2,3.77-5.2,3.77s2.21,10.92,2.11,11.14
	c-0.08,0.15-1.87,1.1-7.49,2.41c-4.59,1.07-7.6,1.11-7.6,1.11s-0.64-3.09-1.04-5.2c-0.44-2.28-1.07-5.01-1.07-5.01
	s-3.65,0.26-9.02-0.4c-4.2-0.51-8.12-1.95-8.12-1.95s0.81,2.22,1.12,4.69c0.23,1.86,0.98,5.67,0.98,5.67s-3.87-1.51-8.28-2.66
	c-4.41-1.15-8.69-1.44-8.69-1.44s-0.45-4.15-0.77-7.41s-0.37-4.82-0.37-4.82s-4.8-1.8-9.4-2.02C32.68,6.72,28.24,8.23,28.24,8.23z"
	/>
<path style="fill:#F1EDEC;" d="M30.29,22.28c-0.07,0.28,1.1,12.24,1.25,12.35c0.15,0.11,4.56-0.87,8.85-1.25s9.97,0.01,9.97,0.01
	l-1.91-11.94c0,0-5.48-0.96-9.54-0.8C34.85,20.8,30.33,22.13,30.29,22.28z"/>
<path style="fill:#F1EDEC;" d="M33.76,48.5l2.15,13.3c0,0,3.99-1.56,9.12-2.2s9.75-0.26,9.75-0.26l-2.36-12.75
	c0,0-5.24-0.67-9.83-0.28C37.17,46.75,33.76,48.5,33.76,48.5z"/>
<path style="fill:#F1EDEC;" d="M50.67,33.94l1.96,12.16c0,0,5.36,0.57,8.29,1.14c2.93,0.57,8.23,1.71,8.35,1.6
	c0.11-0.11-1.34-11.29-1.76-11.67c-0.42-0.38-4.68-1.25-7.98-1.94C56.21,34.55,50.7,33.56,50.67,33.94z"/>
<path style="fill:#F1EDEC;" d="M65.89,25.63c-0.12,0.06,1.37,10.75,1.86,11.25c0.49,0.49,5.35,1.37,8.1,1.74
	c4.02,0.53,8.9,0.55,9.09,0.44c0.19-0.11-2.4-11.11-2.44-11.18c-0.16-0.25-2.66,0.15-7.9-0.42C69.28,26.87,66.04,25.55,65.89,25.63z
	"/>
<path style="fill:#F1EDEC;" d="M85.31,39.34c-0.14,0.14,2.12,12.44,2.25,12.61c0.31,0.41,4.56-0.15,7.67-0.72
	c4.55-0.83,8.01-2.13,8.01-2.13s-2.56-12.76-2.68-12.87c-0.11-0.11-3.41,1.47-7.88,2.27C88.04,39.34,85.5,39.15,85.31,39.34z"/>
<path style="fill:#F1EDEC;" d="M69.78,49.32c-0.04,0.23,2.39,12.58,2.46,13.06c0.04,0.25,3.92,1.13,7.83,1.7
	c4.45,0.65,9.82,0.79,9.82,0.79s-1.18-5.38-1.61-7.86c-0.45-2.56-0.94-4.79-0.94-4.79s-4.57-0.38-8.98-1.06
	C73.95,50.48,69.78,49.32,69.78,49.32z"/>
      </g>
    </svg>
  `)}`;
  
  if (!window.google?.maps) return { url: svgIcon };
  return {
    url: svgIcon,
    scaledSize: new window.google.maps.Size(28, 28),
    anchor: new window.google.maps.Point(14, 14)
  };
};

// Styled components matching BeginShift
const Container = styled(Box)({
  padding: '16px',
  maxWidth: '100vw',
  margin: '0 auto',
  backgroundColor: '#f8f9fa',
  minHeight: '100vh',
  paddingBottom: '80px',
});

const HeaderCard = styled(Card)({
  marginBottom: '16px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  borderRadius: '8px',
});

const CompleteShift = ({ 
  navigateTo, 
  userSettings, 
  fleetSettings, 
  socket,
  routeDetails,
  vehicleDetails,
  selectedVehicle,
  driverAuthData,
  inspectionData,
  googleMapsLoaded,
  onShiftComplete 
}) => {
  // Debug: Track re-renders
  const renderCountRef = useRef(0);
  const prevPropsRef = useRef();
  
  renderCountRef.current += 1;
  console.log('CompleteShift - Render count:', renderCountRef.current);
  
  // Track what props changed to cause re-render
  useEffect(() => {
    if (prevPropsRef.current) {
      const prevProps = prevPropsRef.current;
      const changedProps = [];
      
      if (prevProps.googleMapsLoaded !== googleMapsLoaded) changedProps.push('googleMapsLoaded');
      if (prevProps.fleetSettings !== fleetSettings) changedProps.push('fleetSettings');
      if (prevProps.routeDetails !== routeDetails) changedProps.push('routeDetails');
      if (prevProps.selectedVehicle !== selectedVehicle) changedProps.push('selectedVehicle');
      if (prevProps.vehicleDetails !== vehicleDetails) changedProps.push('vehicleDetails');
      if (prevProps.socket !== socket) changedProps.push('socket');
      if (prevProps.userSettings !== userSettings) changedProps.push('userSettings');
      
      if (changedProps.length > 0) {
        console.log('CompleteShift - Props changed:', changedProps);
      }
    }
    
    prevPropsRef.current = {
      googleMapsLoaded,
      fleetSettings,
      routeDetails,
      selectedVehicle,
      vehicleDetails,
      socket,
      userSettings
    };
  });

  const [odometerReading, setOdometerReading] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [odometerError, setOdometerError] = useState('');
  const [inspectionRequired, setInspectionRequired] = useState(false);
  const [inspectionItems, setInspectionItems] = useState([]);
  const [inspectionFormData, setInspectionFormData] = useState({});
  const [isFormValid, setIsFormValid] = useState(true);
  const [showOdometerConfirm, setShowOdometerConfirm] = useState(false);

  // Compute min and max odometer values based on vehicle mileage and current shift's outbound reading
  // Uses configurable maxOdometerRange from fleetSettings (default 1000 miles)
  // If no previous reading exists (minValue === 0), any positive number is allowed
  // Backwards compatible: old servers include prevOutboundMileage/prevInboundMileage on shiftObject
  const { minOdometerValue, maxOdometerValue, hasPreviousReading } = useMemo(() => {
    const shiftObject = driverAuthData?.dispatchData?.shiftObject;
    const vehicleObject = driverAuthData?.dispatchData?.vehicleObject || driverAuthData?.vehicleObject;
    const vehicleMileage = parseFloat(vehicleObject?.mileage || vehicleObject?.Mileage) || 0;
    const currentOutbound = parseFloat(shiftObject?.outboundMileage) || 0;
    // Old servers provided prev shift readings on shiftObject; new servers rely on vehicleMileage
    const prevOutbound = parseFloat(shiftObject?.prevOutboundMileage) || 0;
    const prevInbound = parseFloat(shiftObject?.prevInboundMileage) || 0;
    
    // For ending odometer, use the maximum of all available previous readings
    const minValue = Math.max(vehicleMileage, currentOutbound, prevOutbound, prevInbound);
    
    // If no previous reading, allow any positive number (no bounds enforcement)
    if (minValue === 0) {
      console.log('CompleteShift: No previous odometer reading - any positive value allowed');
      return { minOdometerValue: 0, maxOdometerValue: null, hasPreviousReading: false };
    }
    
    // Use fleet configurable max range, default to 1000 miles
    const maxRange = fleetSettings?.maxOdometerRange || 1000;
    const maxValue = minValue + maxRange;
    
    console.log('CompleteShift: Calculated odometer range:', {
      vehicleMileage,
      currentOutbound,
      prevOutbound,
      prevInbound,
      minValue,
      maxValue,
      maxRange
    });
    
    return { minOdometerValue: minValue, maxOdometerValue: maxValue, hasPreviousReading: true };
  }, [driverAuthData, fleetSettings?.maxOdometerRange]);
  
  // Map and GPS state
  const [currentLocation, setCurrentLocation] = useState(null);
  const [endShiftDirections, setEndShiftDirections] = useState(null);
  const [isAccurate, setIsAccurate] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 28.05, lng: -82.4483 }); // Stable map center
  const [mapReady, setMapReady] = useState(false);
  
  // GPS tracking refs for timer-based updates (like Shuttlebus components)
  const lastLocationRef = useRef(null);
  const gpsIntervalRef = useRef(null);
  
  // Direction calculation throttling ref (max once every 2 minutes)
  const lastDirectionCalculation = useRef(null);
  
  // Generate consistent color for a vehicle based on its ID (same as WaypointMap components)
  const generateVehicleColor = useCallback((vehicleId) => {
    if (!vehicleId) return '#6B7280'; // Default gray
    
    // Create a hash from the vehicleId
    let hash = 0;
    const idString = String(vehicleId);
    for (let i = 0; i < idString.length; i++) {
      hash = idString.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generate HSL color with good saturation and lightness for visibility
    const hue = Math.abs(hash) % 360;
    const saturation = 70 + (Math.abs(hash) % 20); // 70-90%
    const lightness = 40 + (Math.abs(hash) % 15); // 40-55%
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }, []);
  
  // Get Google API key from fleet settings
  const googleApiKey = fleetSettings?.googleApiKey || 
    userSettings?.settings?.GoogleAPIKey || 
    localStorage.getItem('googleAPIKey');

  // Memoize map options to prevent re-creation
  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    clickableIcons: false,
    keyboardShortcuts: false,
    gestureHandling: 'greedy',
  }), []);

  // Memoize vehicle icon to prevent re-creation with consistent coloring
  const vehicleIcon = useMemo(() => {
    // Only create icon if Google Maps is loaded
    if (!window.google?.maps || !googleMapsLoaded) {
      console.log('CompleteShift - Google Maps not ready for vehicle icon');
      return null;
    }
    
    // Try to get vehicleId for consistent coloring
    const vehicleId = selectedVehicle?.VehicleId || selectedVehicle?.vehicleId || vehicleDetails?.VehicleId || vehicleDetails?.vehicleId;
    const vehicleColor = vehicleId ? generateVehicleColor(vehicleId) : '#2563EB';
    
    console.log('CompleteShift - Creating vehicle icon with color:', vehicleColor, 'for vehicleId:', vehicleId);
    return createVehicleIcon(vehicleColor);
  }, [googleMapsLoaded, selectedVehicle, vehicleDetails, generateVehicleColor]);

  // Memoize finish flag icon to prevent re-creation
  const finishFlagIcon = useMemo(() => {
    // Only create icon if Google Maps is loaded
    if (!window.google?.maps?.Size || !googleMapsLoaded) {
      console.log('CompleteShift - Google Maps not ready for finish flag icon');
      return null;
    }
    
    console.log('CompleteShift - Creating finish flag icon');
    return createFinishFlagIcon();
  }, [googleMapsLoaded]); // Re-create when Google Maps loads

  // Memoize polyline options for end shift route display
  const directionsOptions = useMemo(() => ({
    strokeColor: '#8B5CF6', // Purple color for end shift routing
    strokeWeight: 4,
    strokeOpacity: 0.8,
  }), []);

  // Debug logging for map rendering
  useEffect(() => {
    console.log('CompleteShift - Debug state:', {
      googleApiKey: !!googleApiKey,
      googleApiKeyValue: googleApiKey,
      googleMapsLoaded: !!googleMapsLoaded,
      googleMapsLoadedValue: googleMapsLoaded,
      currentLocation: !!currentLocation,
      currentLocationValue: currentLocation,
      fleetSettings: !!fleetSettings,
      routeDetails: !!routeDetails,
      endShiftDirections: !!endShiftDirections,
      routeEndCoords: routeDetails ? {
        endLat: routeDetails.endLat || routeDetails.EndLat,
        endLon: routeDetails.endLon || routeDetails.EndLon
      } : null,
      // Check the actual condition
      shouldShowMap: !!(googleApiKey && googleMapsLoaded && currentLocation)
    });
  }, [googleApiKey, googleMapsLoaded, currentLocation, fleetSettings, routeDetails, endShiftDirections]);

  useEffect(() => {
    // Load end-shift inspection items from inspectionData prop
    if (inspectionData && inspectionData.data && inspectionData.data.length > 0) {
      // Filter for EndShift items (isenditem === 1)
      const endShiftItems = inspectionData.data.filter(item => item.isenditem === 1);
      setInspectionItems(endShiftItems);
      
      // Check if inspection is required based on presence of end-shift items
      const requiresInspection = endShiftItems.length > 0;
      setInspectionRequired(requiresInspection);
      
      // Initialize form data for end-shift items
      const initialFormData = {};
      endShiftItems.forEach((item, index) => {
        initialFormData[`input-${index}`] = item.type === 'boolean' ? false : '';
      });
      setInspectionFormData(initialFormData);
    } else {
      // No inspection data or no end-shift items, so no inspection required
      setInspectionRequired(false);
      setInspectionItems([]);
      setInspectionFormData({});
    }
  }, [inspectionData]);

  // Validate required inspection items
  useEffect(() => {
    if (inspectionItems.length === 0) {
      setIsFormValid(true);
      return;
    }

    const allRequiredCompleted = inspectionItems.every((item, index) => {
      if (!item.required) return true;
      const value = inspectionFormData[`input-${index}`];
      return value !== '' && value !== false && value !== undefined && value !== null;
    });

    setIsFormValid(allRequiredCompleted);
  }, [inspectionItems, inspectionFormData]);

  // GPS update functionality (similar to Shuttlebus components)
  const sendGPSUpdate = useCallback((location) => {
    // Send GPS update for route completion tracking
    if (selectedVehicle && routeDetails) {
      const routeGroupId = routeDetails.FixedRouteGroupId || routeDetails.RouteMasterGroupId;
      if (routeGroupId) {
        console.log('CompleteShift - Sending GPS_UPDATE:', {
          lat: location.lat,
          lng: location.lng,
          vehicleId: selectedVehicle.id || selectedVehicle.Id,
          vehicleObject: selectedVehicle,
          routeId: routeDetails.routeId,
          groupId: routeGroupId
        });
        
        comms.sendGPSUpdate(
          location.lat,
          location.lng,
          location.speed ?? 0, // speed (mph)
          selectedVehicle.id || selectedVehicle.Id,
          routeDetails.routeId,
          routeDetails.routeType || (routeDetails.FixedRouteId ? 'fixedroute' : 'route'),
          routeGroupId,
          selectedVehicle // Pass the complete vehicle object
        );
      } else {
        console.log('CompleteShift - GPS_UPDATE not sent - missing routeGroupId');
      }
    } else {
      console.log('CompleteShift - GPS_UPDATE not sent - missing requirements');
    }
  }, [selectedVehicle, routeDetails]);

  // Set up 10-second GPS update timer (like Shuttlebus components)
  useEffect(() => {
    if (selectedVehicle && routeDetails) {
      const routeGroupId = routeDetails.FixedRouteGroupId || routeDetails.RouteMasterGroupId;
      if (routeGroupId) {
        console.log('CompleteShift - Starting 10-second GPS timer');
        
        gpsIntervalRef.current = setInterval(() => {
          if (lastLocationRef.current) {
            console.log('CompleteShift - Timer-triggered GPS update');
            sendGPSUpdate(lastLocationRef.current);
          }
        }, 10000); // 10 seconds

        return () => {
          if (gpsIntervalRef.current) {
            console.log('CompleteShift - Clearing GPS timer');
            clearInterval(gpsIntervalRef.current);
            gpsIntervalRef.current = null;
          }
        };
      }
    }
  }, [selectedVehicle, routeDetails, sendGPSUpdate]);

  // Calculate directions from current GPS location to route end point using OSRM/Google
  const calculateEndShiftDirections = useCallback(async (currentPos) => {
    if (!currentPos || !routeDetails) {
      return;
    }

    // Throttle API calls - only call once every 2 minutes
    const now = Date.now();
    const twoMinutes = 2 * 60 * 1000;
    
    if (lastDirectionCalculation.current && (now - lastDirectionCalculation.current) < twoMinutes) {
      console.log('End shift directions already calculated recently, skipping API call');
      return;
    }

    // Get route end coordinates
    const endLat = routeDetails.endLat || routeDetails.EndLat || routeDetails.end_lat;
    const endLon = routeDetails.endLon || routeDetails.EndLon || routeDetails.end_lon;

    if (!endLat || !endLon || isNaN(parseFloat(endLat)) || isNaN(parseFloat(endLon))) {
      console.log('No valid end coordinates found in route details');
      return;
    }

    lastDirectionCalculation.current = now;

    const origin = { lat: currentPos.lat, lng: currentPos.lng };
    const destination = { lat: parseFloat(endLat), lng: parseFloat(endLon) };
    const osrmUrl = fleetSettings?.osrmUrl || null;

    try {
      const result = await getDirections({ origin, destination, waypoints: [], osrmUrl });
      if (result && result.path.length > 0) {
        setEndShiftDirections(result.path);
        console.log('End shift directions calculated successfully via', result.source);
      } else {
        setEndShiftDirections(null);
        lastDirectionCalculation.current = null;
      }
    } catch (error) {
      console.error('End shift directions request failed:', error);
      setEndShiftDirections(null);
      lastDirectionCalculation.current = null;
    }
  }, [routeDetails, fleetSettings?.osrmUrl]);

  // Handle GPS location updates
  const handleLocationUpdate = (location) => {
    console.log('CompleteShift - GPS location received:', location);
    // Store the latest location reference for GPS timer
    // Store location in ref for comparison
    const previousLocation = lastLocationRef.current;
    lastLocationRef.current = location;
    
    // Only update currentLocation if it's significantly different to reduce re-renders
    if (!previousLocation || 
        Math.abs(previousLocation.lat - location.lat) > 0.0001 ||
        Math.abs(previousLocation.lng - location.lng) > 0.0001) {
      setCurrentLocation(location);
    }
    
    // Set map center only once when we first get location
    if (!mapCenter || (mapCenter.lat === 28.05 && mapCenter.lng === -82.4483)) {
      console.log('CompleteShift - Setting map center to GPS location');
      setMapCenter(location);
    }    calculateEndShiftDirections(location);
    
    // Send legacy GPS format for backward compatibility
    comms.sendSocket(
      "GPS|N " +
        Math.abs(location.lat) +
        ",W " +
        Math.abs(location.lng) +
        ",0"
    );

    // Send GPS update for group sharing
    sendGPSUpdate(location);
  };

  // Reset mapReady when map conditions change
  useEffect(() => {
    if (!googleApiKey || !googleMapsLoaded || !currentLocation) {
      setMapReady(false);
    }
  }, [googleApiKey, googleMapsLoaded, currentLocation]);

  // Add effect to verify GeolocationHandler is working
  useEffect(() => {
    console.log('CompleteShift - Component mounted, GPS should start...');
    
    // Check for geolocation support
    if (!navigator.geolocation) {
      console.error('CompleteShift - Geolocation is not supported by this browser');
    } else {
      console.log('CompleteShift - Geolocation is supported');
    }
  }, []);

  // Handle map loading
  const onMapLoad = useCallback((mapInstance) => {
    console.log('CompleteShift: Map loaded successfully');
    
    // Set map as ready after a delay to ensure it's fully loaded (same as WaypointMap components)
    setTimeout(() => {
      console.log('CompleteShift: Setting mapReady to true');
      setMapReady(true);
    }, 200);
  }, []);

  const handleOdometerChange = (e) => {
    const newValue = e.target.value;
    setOdometerReading(newValue);
    
    // Validate and set error message
    // Only validate bounds if there is a previous reading to compare against
    const odometerValue = parseFloat(newValue);
    if (!newValue || String(newValue).trim() === '' || isNaN(odometerValue)) {
      setOdometerError('');
    } else if (odometerValue < 0) {
      setOdometerError('Odometer reading must be a positive number');
    } else if (hasPreviousReading && odometerValue < minOdometerValue) {
      setOdometerError(`Below expected minimum (${minOdometerValue.toLocaleString()} mi) — you'll be asked to confirm`);
    } else if (hasPreviousReading && maxOdometerValue !== null && odometerValue > maxOdometerValue) {
      setOdometerError(`Above expected maximum (${maxOdometerValue.toLocaleString()} mi) — you'll be asked to confirm`);
    } else {
      setOdometerError('');
    }
  };

  const handleInspectionInputChange = (e) => {
    const { name, value } = e.target;
    setInspectionFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInspectionCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setInspectionFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleShiftCompletion = async () => {
    setIsSubmitting(true);
    setError('');

    console.log('=== CompleteShift Debug Info ===');
    console.log('driverAuthData:', driverAuthData);
    console.log('vehicleDetails:', vehicleDetails);
    console.log('routeDetails:', routeDetails);
    console.log('userSettings:', userSettings);
    
    // Extract values from consistent data structure
    // NOTE: dispatchData.shiftObject may be empty {} if shift was opened via socket after auth
    // In that case, fall back to driverAuthData.shiftObject (set by Dispatch.js openShift handler)
    const { dispatchData } = driverAuthData;
    const shiftData = (dispatchData.shiftObject && Object.keys(dispatchData.shiftObject).length > 0)
      ? dispatchData.shiftObject
      : (driverAuthData.shiftObject || {});
    const driverData = dispatchData.driverObject;
    const vehicleData = dispatchData.vehicleObject;
    const routeObject = driverAuthData.routeObject;
    
    console.log('Extracted values:');
    console.log('- shiftData:', shiftData);
    console.log('- driverData:', driverData);
    console.log('- vehicleData:', vehicleData);
    console.log('- routeObject:', routeObject);
    console.log('================================');

    try {
      // Prepare inspection data from form and validate required items
      let completionInspectionData = null;
      let allRequiredCompleted = true;
      
      if (inspectionItems.length > 0) {
        const filledItems = [];
        inspectionItems.forEach((item, index) => {
          const value = inspectionFormData[`input-${index}`];
          
          // Check if required item is missing
          if (item.required && (value === '' || value === false || value === undefined || value === null)) {
            allRequiredCompleted = false;
          }
          
          // Add item to results if it has a value
          if (value !== '' && value !== false && value !== undefined && value !== null) {
            filledItems.push({
              name: item.name,
              type: item.type,
              result: value,
              required: item.required || false
            });
          }
        });
        
        // If there are required items that aren't completed, show error and return
        if (!allRequiredCompleted) {
          setIsSubmitting(false);
          setError('Please complete all required end-of-shift inspection items before completing your shift.');
          return;
        }
        
        if (filledItems.length > 0) {
          completionInspectionData = filledItems;
        }
      }

      // Validate odometer reading. Out-of-range values are permitted here because
      // the driver confirms them via the out-of-range dialog before reaching this point.
      if (odometerReading) {
        const odometerValue = parseFloat(odometerReading);
        if (isNaN(odometerValue) || odometerValue < 0) {
          setIsSubmitting(false);
          setError('Please enter a valid ending odometer reading.');
          return;
        }
      }

      // Step 1: Send inspection completion if required (via socket)
      if (inspectionRequired) {
        const inspectionData = {
          vehicleId: vehicleDetails.Id || vehicleDetails.id,
          driverId: userSettings.Id || userSettings.id,
          shiftId: shiftData.Id || shiftData.id || shiftData.shiftId,
          inspectionType: 'post-route',
          completedAt: new Date().toISOString(),
          status: 'completed'
        };

        // Send inspection completion via socket
        if (socket) {
          socket.emit('postInspection', inspectionData, (response) => {
            if (!response.success) {
              console.error('Inspection completion failed:', response.error);
              // Continue with shift completion even if inspection fails
            }
          });
        }
      }

      // Step 2: Unassign vehicle from route if there's a route object
      const handleVehicleUnassignment = () => {
        return new Promise((resolve, reject) => {
          // Check if there's a route object in the dispatch data
          // Null-safe: dispatchData or its children may be missing depending on auth path
          const dispatchData = driverAuthData?.dispatchData;
          const vehicleData = dispatchData?.vehicleObject || driverAuthData?.vehicleObject || selectedVehicle;
          const routeObject = driverAuthData?.routeObject || dispatchData?.routeObject || routeDetails;
          
          if (routeObject && routeObject.routeId && vehicleData) {
            console.log('Unassigning vehicle from route before shift completion:', routeObject.routeId);
            
            const unassignData = {
              routeId: routeObject.routeId,
              vehicleId: vehicleData.Id || vehicleData.id,
              routeType: routeObject.routeType || 'fixedroute'
            };

            socket.emit('unassignVehicleFromRoute', unassignData, (response) => {
              if (response && response.success) {
                console.log('Vehicle unassigned from route successfully');
                resolve();
              } else {
                console.warn('Failed to unassign vehicle from route:', response?.error);
                // Continue with shift completion even if unassignment fails
                resolve();
              }
            });
          } else {
            // No route to unassign from, proceed directly
            resolve();
          }
        });
      };

      // Step 3: Send complete shift request via socket (includes optional odometer and inspection data)
      const completeShiftProcess = async () => {
        try {
          // First, unassign vehicle from route if needed
          await handleVehicleUnassignment();

          // Extract values from consistent data structure
          // NOTE: dispatchData.shiftObject may be empty {} if shift was opened via socket after auth
          // In that case, fall back to driverAuthData.shiftObject (set by Dispatch.js openShift handler)
          const { dispatchData } = driverAuthData;
          const shiftDataSource = (dispatchData?.shiftObject && Object.keys(dispatchData.shiftObject).length > 0)
            ? dispatchData.shiftObject
            : (driverAuthData.shiftObject || {});
          const shiftData = {...shiftDataSource};
          // Null-safe: driverObject/vehicleObject may be in dispatchData or at top level of driverAuthData
          const rawDriverObj = dispatchData?.driverObject || driverAuthData?.driverObject || {};
          const rawVehicleObj = dispatchData?.vehicleObject || driverAuthData?.vehicleObject || selectedVehicle || {};
          const driverData = {...rawDriverObj, Id: rawDriverObj.Id || rawDriverObj.id, id: rawDriverObj.id || rawDriverObj.Id};
          const vehicleData = {...rawVehicleObj, Id: rawVehicleObj.Id || rawVehicleObj.id, id: rawVehicleObj.id || rawVehicleObj.Id};
          const shiftId = shiftData.Id || shiftData.id || shiftData.shiftId;

          // Log data structure for debugging if needed
          if (!shiftId || !driverData || !vehicleData) {
            console.warn('Data structure validation:', {
              hasShiftId: !!shiftId,
              hasDriverData: !!driverData,
              hasVehicleData: !!vehicleData,
              driverAuthDataStructure: Object.keys(driverAuthData),
              dispatchDataStructure: dispatchData ? Object.keys(dispatchData) : 'missing'
            });
          }

          const shiftCompletionData = {
            vehicleId: vehicleData.Id || vehicleData.id,
            routeId: routeDetails?.routeId,
            shiftId: shiftId,
            completionTime: new Date().toISOString(),
            userId: driverData.Id || driverData.id,
            driverObject: driverData,
            vehicleObject: vehicleData
          };

          console.log('Final shift completion data:', shiftCompletionData);
          
          // Check for missing required values
          if (!shiftCompletionData.shiftId) {
            console.error('ERROR: shiftId is missing!');
            throw new Error('Shift ID is required but could not be found in the available data');
          }
          
          if (!shiftCompletionData.vehicleId) {
            console.error('ERROR: vehicleId is missing!');
            throw new Error('Vehicle ID is required but could not be found in the available data');
          }
          
          if (!shiftCompletionData.driverObject) {
            console.error('ERROR: driverObject is missing!');
            throw new Error('Driver object is required but could not be found in the available data');
          }
          
          if (!shiftCompletionData.vehicleObject) {
            console.error('ERROR: vehicleObject is missing!');
            throw new Error('Vehicle object is required but could not be found in the available data');
          }

          // Add optional fields only if they have values
          if (odometerReading.trim()) {
            shiftCompletionData.endOdometer = parseFloat(odometerReading);
          }
          
          if (completionInspectionData) {
            shiftCompletionData.inspectionData = completionInspectionData;
          }

          // #6 Persist the ending odometer via the dedicated HTTP endpoint
          // (best-effort). The socket completeShift below also carries
          // endOdometer; this ensures the reading is recorded even if the
          // socket payload path changes server-side. Does not block completion.
          if (odometerReading.trim() && shiftId) {
            const endingOdometer = parseFloat(odometerReading);
            if (!isNaN(endingOdometer) && endingOdometer >= 0) {
              try {
                await comms.updateShiftOdometer(shiftId, { endingOdometer });
              } catch (odoErr) {
                console.warn('Ending odometer HTTP update failed (continuing):', odoErr);
              }
            }
          }

          if (socket) {
            // Safety timeout - if server never responds, unblock the driver after 30 seconds
            const socketTimeoutId = setTimeout(() => {
              setIsSubmitting(false);
              setError('Server did not respond in time. Please check your connection and try again.');
            }, 30000);

            socket.emit('completeShift', shiftCompletionData, (response) => {
              clearTimeout(socketTimeoutId);
              setIsSubmitting(false);
              
              if (response && response.success) {
                console.log('Shift completed successfully:', response);
                
                // Shift is fully completed on server - use onShiftComplete to clean up and return to login
                // Do NOT navigate to Logout (shift is already closed, vehicle already unassigned)
                if (onShiftComplete) {
                  onShiftComplete({
                    ...response,
                    completionData: shiftCompletionData
                  });
                }
              } else {
                setError(response?.error || response?.message || 'Failed to complete shift. Please try again.');
              }
            });
          } else {
            setIsSubmitting(false);
            throw new Error('No connection to server. Please check your connection and try again.');
          }
        } catch (error) {
          console.error('Error in shift completion process:', error);
          setIsSubmitting(false);
          setError(error.message || 'Failed to complete shift. Please try again.');
        }
      };

      // Execute the completion process
      completeShiftProcess();
    } catch (error) {
      setIsSubmitting(false);
      setError(error.message || 'Failed to complete shift. Please try again.');
    }
  };

  // Returns true when the entered reading is outside the expected sanity-check range.
  const isOdometerOutOfRange = () => {
    if (!hasPreviousReading || !odometerReading) return false;
    const v = parseFloat(odometerReading);
    if (isNaN(v) || v < 0) return false;
    return v < minOdometerValue || (maxOdometerValue !== null && v > maxOdometerValue);
  };

  const handleStartShiftCompletion = () => {
    // Out-of-range odometer requires explicit confirmation before completing the shift
    if (isOdometerOutOfRange()) {
      setShowOdometerConfirm(true);
      return;
    }
    handleShiftCompletion();
  };

  return (
    <Container>
      <HeaderCard>
        <Box p={3}>
 
          {/* GeolocationHandler - Always render to get GPS */}
          <GeolocationHandler
            onLocationUpdate={handleLocationUpdate}
            setIsAccurate={setIsAccurate}
          />

          {/* Map Section - Route to End Point */}
          {(() => {
            const shouldShow = googleApiKey && googleMapsLoaded && currentLocation;
            console.log('CompleteShift - Map render condition:', {
              googleApiKey: !!googleApiKey,
              googleMapsLoaded: !!googleMapsLoaded, 
              currentLocation: !!currentLocation,
              shouldShow: shouldShow
            });
            return shouldShow;
          })() && (
            <Box mb={3}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#1f2937'
              }}>
                Route to Main Lot
              </Typography>
              <Box sx={{ 
                height: '250px', 
                borderRadius: '8px', 
                overflow: 'hidden',
                border: '1px solid #d1d5db'
              }}>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={mapCenter}
                  zoom={13}
                  onLoad={onMapLoad}
                  onZoomChanged={() => {
                    console.log('CompleteShift - Map zoom changed');
                  }}
                  onCenterChanged={() => {
                    console.log('CompleteShift - Map center changed');
                  }}
                  onBoundsChanged={() => {
                    console.log('CompleteShift - Map bounds changed');
                  }}
                  options={mapOptions}
                >

                  
                  {/* Vehicle marker - only render when map is ready */}
                  {mapReady && currentLocation && vehicleIcon && (
                    <Marker
                      position={currentLocation}
                      icon={vehicleIcon}
                      title={selectedVehicle?.VehicleId || selectedVehicle?.vehicleId ? `Vehicle ${selectedVehicle.VehicleId || selectedVehicle.vehicleId}` : "Your vehicle"}
                      zIndex={1000}
                      animation={window.google.maps.Animation.DROP}
                    />
                  )}
                  
                  {/* Main lot end point marker - only render when map is ready */}
                  {mapReady && routeDetails && (routeDetails.endLat || routeDetails.EndLat) && finishFlagIcon && (
                    <Marker
                      position={{
                        lat: parseFloat(routeDetails.endLat || routeDetails.EndLat),
                        lng: parseFloat(routeDetails.endLon || routeDetails.EndLon)
                      }}
                      icon={finishFlagIcon}
                      title="Finish - Main Lot (Return Location)"
                      zIndex={900}
                    />
                  )}
                  
                  {/* Directions from current location to end point - only render when map is ready */}
                  {mapReady && endShiftDirections && endShiftDirections.length > 1 && (
                    <Polyline
                      path={endShiftDirections}
                      options={directionsOptions}
                    />
                  )}
                </GoogleMap>
              </Box>
            </Box>
          )}

          {/* Map loading message when Google Maps hasn't loaded yet */}
          {(!googleApiKey || !googleMapsLoaded) && (
            <Box mb={3}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#1f2937'
              }}>
                Route to Main Lot
              </Typography>
              <Box sx={{ 
                height: '250px', 
                borderRadius: '8px', 
                border: '1px solid #d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 10px'
                  }}></div>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {!googleApiKey ? 'Loading API key...' : 'Loading Google Maps...'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 1 }}>
                    (API: {googleApiKey ? '✓' : '✗'}, Maps: {googleMapsLoaded ? '✓' : '✗'})
                  </Typography>
                </div>
              </Box>
            </Box>
          )}

          {googleApiKey && googleMapsLoaded && !currentLocation && (
            <Box mb={3}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#1f2937'
              }}>
                Route to Main Lot
              </Typography>
              <Box sx={{ 
                height: '250px', 
                borderRadius: '8px', 
                border: '1px solid #d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 10px'
                  }}></div>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Getting your location...
                  </Typography>
                </div>
              </Box>
            </Box>
          )}

          {/* Odometer Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              mb: 1,
              color: '#1f2937'
            }}>
              Ending Odometer Reading
            </Typography>
            <TextField
              type="number"
              value={odometerReading}
              onChange={handleOdometerChange}
              placeholder={hasPreviousReading ? `Enter mileage (min: ${minOdometerValue.toLocaleString()})` : "Enter ending mileage"}
              fullWidth
              size="small"
              disabled={isSubmitting}
              error={!!odometerError}
              inputProps={{ 
                min: hasPreviousReading ? minOdometerValue : 0, 
                max: hasPreviousReading && maxOdometerValue ? maxOdometerValue : undefined, 
                step: 0.1 
              }}
              helperText={odometerError || (hasPreviousReading 
                ? `Valid range: ${minOdometerValue.toLocaleString()} - ${maxOdometerValue.toLocaleString()} miles`
                : "Enter ending odometer reading to nearest tenth of a mile"
              )}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  fontSize: '16px',
                  '& fieldset': {
                    borderColor: '#d1d5db',
                    borderWidth: '2px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
              }}
            />
          </Box>

          {/* End-of-shift inspection items section */}
          {inspectionItems.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#1f2937',
                fontSize: '18px'
              }}>
                End-of-Shift Inspection Items
              </Typography>
              <Box sx={{

              }}>
                {inspectionItems.map((item, index) => (
                  <Box key={index} mb={2}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      color: '#374151'
                    }}>
                      {item.name}
                      {!!item.required && <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>}
                    </Typography>
                    {item.type === 'boolean' ? (
                      <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          name={`input-${index}`}
                          checked={inspectionFormData[`input-${index}`] || false}
                          onChange={handleInspectionCheckboxChange}
                          disabled={isSubmitting}
                          style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                        />
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          Check if applicable
                        </Typography>
                      </Box>
                    ) : (
                      <TextField
                        name={`input-${index}`}
                        value={inspectionFormData[`input-${index}`] || ''}
                        onChange={handleInspectionInputChange}
                        placeholder={`Enter ${item.name.toLowerCase()}`}
                        disabled={isSubmitting}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: '14px',
                            '& fieldset': {
                              borderColor: '#d1d5db',
                            },
                          },
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {error && (
            <Box sx={{
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '8px',
              mb: 2,
              border: '1px solid #fca5a5'
            }}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          {/* Bottom Buttons */}
          <Box display="flex" gap={2} mt={3}>

            <Button
              variant="contained"
              color="error"
              onClick={handleStartShiftCompletion}
              disabled={isSubmitting || !isFormValid}
              sx={{ 
                flex: 1,
                height: '48px',
                fontSize: '16px',
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              {isSubmitting ? 'Completing Shift...' : 'Complete Shift'}
            </Button>
          </Box>


        </Box>
      </HeaderCard>

      {/* Out-of-range odometer confirmation */}
      <Dialog open={showOdometerConfirm} onClose={() => setShowOdometerConfirm(false)}>
        <DialogTitle>Confirm Odometer Reading</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The reading you entered ({Number(odometerReading || 0).toLocaleString()} mi) is outside the
            expected range{hasPreviousReading && maxOdometerValue !== null
              ? ` (${minOdometerValue.toLocaleString()} – ${maxOdometerValue.toLocaleString()} mi)`
              : ''}. Please confirm this is the correct reading.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOdometerConfirm(false)} color="inherit">
            Go Back
          </Button>
          <Button
            onClick={() => { setShowOdometerConfirm(false); handleShiftCompletion(); }}
            variant="contained"
            color="error"
          >
            Confirm Reading
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Wrap with React.memo to prevent unnecessary re-renders
const MemoizedCompleteShift = React.memo(CompleteShift, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders when props haven't meaningfully changed
  return (
    prevProps.googleMapsLoaded === nextProps.googleMapsLoaded &&
    prevProps.fleetSettings?.googleApiKey === nextProps.fleetSettings?.googleApiKey &&
    prevProps.routeDetails?.endLat === nextProps.routeDetails?.endLat &&
    prevProps.routeDetails?.endLon === nextProps.routeDetails?.endLon &&
    prevProps.routeDetails?.EndLat === nextProps.routeDetails?.EndLat &&
    prevProps.routeDetails?.EndLon === nextProps.routeDetails?.EndLon &&
    prevProps.selectedVehicle === nextProps.selectedVehicle &&
    prevProps.vehicleDetails === nextProps.vehicleDetails &&
    prevProps.driverAuthData === nextProps.driverAuthData
  );
});

export default MemoizedCompleteShift;
